import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import localStore from 'store'
import * as Sentry from '@sentry/react'
import { getOrCreateDeviceId } from 'utils/cookies'
import { posDeviceStatusKey, usePosDeviceStatus } from 'utils/queries/posDevices'
import { POS_ONLINE_HEADER } from 'utils/static'
import { getActiveEndpoint } from '@/utils/posLocalDb/queueContext'

// { [endpoint]: deviceId } — browser yang sudah terdaftar per tenant, supaya cek
// status (hit /inits) tidak diulang setiap buka kasir.
const REGISTERED_KEY = 'kledo.pos.registeredDevices'

const STATUS_CONFIG = { ...POS_ONLINE_HEADER, skipErrorNotification: true }

const readRegistered = () => {
  const value = localStore.get(REGISTERED_KEY)
  return value && typeof value === 'object' ? value : {}
}

const rememberRegistered = (endpoint, deviceId) => {
  localStore.set(REGISTERED_KEY, { ...readRegistered(), [endpoint]: deviceId })
}

/**
 * Gate pendaftaran device POS, dijalankan setelah outlet dipilih (`enabled`).
 * - `checking`: status belum diketahui (hit pertama per browser + tenant).
 * - `unregistered`: BE melaporkan `registered === false` → wajib daftar.
 * - `ready`: terdaftar, BE tidak mengirim `pos_device` (sesi/BE lama), atau cek gagal
 *   (fail-open + Sentry, sama dengan cek device saat tutup shift).
 */
const usePosDeviceGate = (enabled) => {
  const queryClient = useQueryClient()
  const endpoint = getActiveEndpoint()
  const deviceId = getOrCreateDeviceId()
  const [registeredVersion, setRegisteredVersion] = useState(0)

  const cached = useMemo(
    () => readRegistered()[endpoint] === deviceId,
    // registeredVersion: baca ulang setelah pendaftaran sukses
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, deviceId, registeredVersion],
  )

  const { data, isLoading, error } = usePosDeviceStatus(
    { endpoint, deviceId, apiOptions: STATUS_CONFIG },
    { enabled: !!enabled && !!endpoint && !cached },
  )

  useEffect(() => {
    if (data?.registered === true) {
      rememberRegistered(endpoint, deviceId)
    }
  }, [data, endpoint, deviceId])

  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  const markRegistered = useCallback(
    (nickname) => {
      rememberRegistered(endpoint, deviceId)
      queryClient.setQueryData(posDeviceStatusKey(endpoint, deviceId), (prev) => ({
        ...(prev || {}),
        device_id: deviceId,
        registered: true,
        nickname,
      }))
      setRegisteredVersion((v) => v + 1)
    },
    [deviceId, endpoint, queryClient],
  )

  let status = 'ready'
  if (enabled && !cached) {
    if (isLoading) {
      status = 'checking'
    } else if (data?.registered === false) {
      status = 'unregistered'
    }
  }

  return { status, deviceId, markRegistered }
}

export default usePosDeviceGate
